import { useMemo } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { dataHojeISO, usePedidosStore } from './store';
import { cn } from '@/lib/cn';

const DIAS_SEMANA = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
const MESES = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

function parseISO(d: string): Date | null {
  if (!d || d === 'all') return null;
  const [y, m, day] = d.split('-').map(Number);
  if (!y || !m || !day) return null;
  return new Date(y, m - 1, day);
}

function toISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function rotularData(iso: string): string {
  if (iso === 'all') return 'Todos os dias';
  const d = parseISO(iso);
  if (!d) return iso;
  const hoje = parseISO(dataHojeISO())!;
  const diff = Math.round((d.getTime() - hoje.getTime()) / 86400000);
  if (diff === 0) return 'Hoje';
  if (diff === -1) return 'Ontem';
  if (diff === 1) return 'Amanhã';
  return `${DIAS_SEMANA[d.getDay()]}, ${d.getDate()} de ${MESES[d.getMonth()]}`;
}

export function DateNav() {
  const data = usePedidosStore((s) => s.data);
  const setData = usePedidosStore((s) => s.setData);
  const carregando = usePedidosStore((s) => s.carregando);

  const dt = useMemo(() => parseISO(data), [data]);
  const isHoje = data === dataHojeISO();
  const isAll = data === 'all';

  function navegar(dias: number) {
    if (!dt) return;
    const novo = new Date(dt);
    novo.setDate(novo.getDate() + dias);
    setData(toISO(novo));
  }

  return (
    <div className="flex items-center gap-s2">
      <button
        type="button"
        onClick={() => navegar(-1)}
        disabled={isAll || carregando}
        className={cn(
          'inline-flex items-center justify-center w-[36px] h-[36px] rounded-sm border border-[var(--border)] text-fg-2',
          'hover:bg-card hover:text-fg-1 hover:border-[var(--border-strong)] transition-all duration-fast',
          'disabled:opacity-40 disabled:cursor-not-allowed',
        )}
        aria-label="dia anterior"
      >
        <ChevronLeft size={16} strokeWidth={1.5} />
      </button>

      <div
        className={cn(
          'inline-flex items-center gap-s3 h-[36px] px-s4 rounded-sm border bg-card',
          isHoje ? 'border-moss-400' : 'border-[var(--border)]',
        )}
      >
        <Calendar size={14} strokeWidth={1.5} className={isHoje ? 'text-moss-300' : 'text-fg-3'} />
        <span className="font-body font-semibold text-[13px] text-fg-1 min-w-[150px]">
          {rotularData(data)}
        </span>
        <input
          type="date"
          value={isAll ? '' : data}
          onChange={(e) => {
            if (e.target.value) setData(e.target.value);
          }}
          className="bg-transparent border-0 outline-none text-fg-3 text-[12px] font-mono cursor-pointer hover:text-fg-1 transition-colors"
          style={{ colorScheme: 'dark', width: 22 }}
          aria-label="escolher data"
        />
      </div>

      <button
        type="button"
        onClick={() => navegar(1)}
        disabled={isAll || carregando}
        className={cn(
          'inline-flex items-center justify-center w-[36px] h-[36px] rounded-sm border border-[var(--border)] text-fg-2',
          'hover:bg-card hover:text-fg-1 hover:border-[var(--border-strong)] transition-all duration-fast',
          'disabled:opacity-40 disabled:cursor-not-allowed',
        )}
        aria-label="próximo dia"
      >
        <ChevronRight size={16} strokeWidth={1.5} />
      </button>

      {!isHoje && (
        <button
          type="button"
          onClick={() => setData(dataHojeISO())}
          className="ml-s2 font-body text-[12px] font-semibold text-moss-300 hover:text-moss-200 transition-colors"
        >
          Hoje
        </button>
      )}

      <button
        type="button"
        onClick={() => setData(isAll ? dataHojeISO() : 'all')}
        className={cn(
          'ml-s2 font-body text-[11px] font-semibold uppercase tracking-[0.1em] px-s3 py-[6px] rounded-pill border transition-colors duration-fast',
          isAll
            ? 'border-moss-400 text-moss-300 bg-[rgba(149,184,134,0.08)]'
            : 'border-[var(--border)] text-fg-3 hover:text-fg-2 hover:border-[var(--border-strong)]',
        )}
        title="Mostrar pedidos de todos os dias"
      >
        Todos
      </button>
    </div>
  );
}

export default DateNav;
